import { BookOpen, FileText, Info, ListChecks, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { manualTaskGuideResponseSchema, type AssistantNextAction, type ManualTaskGuide, type ManualTaskGuideResponse } from "@control-plane/contracts";
import { PageHeader } from "../components/PageHeader";
import { RemotePageState } from "../components/RemotePageState";
import { askAssistant } from "../components/AssistantPanel";
import { api } from "../lib/api";
import { appNavigate } from "../lib/navigate";
import { useRemote } from "../lib/useRemote";

type TableRow = Array<string | number>;
type DocBlock =
  | { type: "p"; text: string }
  | { type: "steps"; title: string; items: string[] }
  | { type: "fields"; title: string; head: TableRow; rows: TableRow[] }
  | { type: "note"; title: string; text: string }
  | { type: "table"; head: TableRow; rows: TableRow[] };

type DocSection = { id: string; anchor: string; title: string; intro: string; blocks: DocBlock[] };

const sections: DocSection[] = [
  {
    id: "login", anchor: "login", title: "登录与账号安全", intro: "登录总管后台并为当前账号启用双因素认证。",
    blocks: [
      { type: "p", text: "访问控制面 Web 地址（本地预览地址由开发人员提供，生产为部署域名）。未登录时先显示「正在验证独立控制面会话」加载态，随后进入登录页。" },
      { type: "steps", title: "登录操作步骤", items: [
        "在左侧面板输入管理员邮箱（自动联想用户名）。",
        "输入当前管理员密码。新建管理员密码至少 10 位，并必须同时包含大写字母、小写字母和数字。",
        "点击「安全登录」按钮，验证中按钮显示「正在验证…」。",
        "登录失败时页面顶部会显示服务端错误信息，可修改后重试。",
        "若该管理员已启用双因素认证，第一次密码验证通过后系统会弹出提示「请输入身份验证器中的 6 位动态码」，输入后点击「验证」完成登录。",
      ] },
      { type: "note", title: "失败保护", text: "连续失败 5 次将锁定账号 15 分钟，期限内即使密码正确也无法登录。", },
    ],
  },
  {
    id: "security", anchor: "security", title: "账号安全", intro: "启用手机身份验证器双因素认证。",
    blocks: [
      { type: "p", text: "「账号安全」页对当前登录管理员本人生效，不能替其他管理员配置。页面由单张身份验证器工作台承载。" },
      { type: "steps", title: "启用双因素认证", items: [
        "进入左侧「账号安全」页。",
        "点击「生成验证二维码」按钮，系统生成本次绑定专用的一次性密钥和扫码链接。",
        "二维码生成后可用 Microsoft Authenticator / Google Authenticator / 1Password 等兼容应用扫描绑定。",
        "无法扫码时，点击一次性密钥旁的复制按钮，把密钥手动输入到验证器中。",
        "在页面上输入手机上显示的最新 6 位动态验证码，点击「验证并启用双重验证」。",
        "成功后页面显示「双重验证已确认，下次登录必须输入动态验证码」。",
      ] },
      { type: "note", title: "安全注意", text: "二维码与一次性密钥只在本次配置过程中显示，页面明确提示请勿截图或转发，完成配置后不再回显。", },
    ],
  },
  {
    id: "dashboard", anchor: "dashboard", title: "全局总览（首页）", intro: "登录后默认首页，一屏查看平台风险与阻塞，再决定处理顺序。所有数字实时来自控制面数据库。",
    blocks: [
      { type: "p", text: "页面固定展示两组数据：指标区 4 张卡片 + 下方两张态势面板，均为只读视图、无表单。" },
      { type: "table", head: ["指标", "显示内容", "口径说明"], rows: [
        ["租户总数", "租户总数 + 附注「X 个运行中」", "来自租户表实时统计；active 数量只统计运行中租户"],
        ["有效服务账号", "有效服务账号数 + 附注「X 个有效订阅」", "状态为 active 的服务账号数量；订阅为有效状态的租户订阅数"],
        ["待投递事件", "待投递事件数 + 附注「X 个死信」", "事件投递队列中还没发出去的事件；死信表示重试多次仍失败的事件"],
        ["开放事故", "开放事故数 + 附注「X 条 24h 审计」", "状态非已解决（resolved）的事故；近 24 小时审计事件总数"],
      ] },
      { type: "steps", title: "当前控制面态势面板", items: [
        "租户运行：显示「运行中/总数」，有暂停租户时附注「X 个已暂停」。",
        "凭据安全：显示已吊销数量与「X 个即将到期」（凭据有效期临近的凭据数）。",
        "代管审批：显示开放中的受控代管授权数，提示需要双人审批与时限。",
      ] },
      { type: "steps", title: "管理员与密钥面板", items: [
        "显示活跃平台管理员总数（大数字）。",
        "点击「检查管理员边界」链接跳转到「平台管理员」页，核对管理员身份与租户账号分离情况。",
      ] },
      { type: "p", text: "页面数据加载失败或请求出错时，错误态面板提供「重试」按钮重新拉取。" },
    ],
  },
  {
    id: "tenants", anchor: "tenants", title: "租户控制", intro: "查看租户使用状态和自动接入进度。注册成功后的基础版资格由系统发放；暂停、恢复、归档仍需填写原因并写入审计链。正式 active 状态继续受平台安全门禁保护。",
    blocks: [
      { type: "steps", title: "搜索与新建租户", items: [
        "顶部搜索框：输入租户名称后按回车，列表按关键词实时过滤；清空搜索恢复全量列表。",
        "点击右上角「新建租户」打开弹窗，填写下列字段后点「创建租户」。",
      ] },
      { type: "fields", title: "新建租户表单字段", head: ["字段", "填写内容 / 示例", "要求与约束"], rows: [
        ["企业名称", "如：某某贸易公司", "必填，2–120 字符，显示名"],
        ["平台负责人", "从在岗管理员中选择", "可暂不指派，创建后可在使用中心维护"],
        ["企业联系人 / 邮箱 / 手机", "如：王经理 / 邮箱 / 11 位手机号", "选填；用于重复检查和后续通知，邮箱手机号不能与其他企业重复"],
        ["初始套餐", "从在售套餐版本中选择", "必选；创建时系统自动建立真实订阅并按套餐发放功能与额度"],
        ["数据区域", "华东（cn-east）/ 华南（cn-south）/ 华北（cn-north）", "必选数据区域，用于区域隔离与部署规划"],
      ] },
      { type: "note", title: "新建后的状态", text: "新租户先进入「配置中」（provisioning），基础版资格由系统自动处理；平台内部安全检查继续推进，但运营无需逐项授权或填写技术证据。", },
      { type: "steps", title: "租户列表", items: [
        "每行显示租户名称、状态徽标、套餐/区域、系统检查进度、处理状态和创建时间。",
        "点击该行右侧「使用中心」，直接查看客户使用结论、基础版策略和系统安全状态。",
      ] },
      { type: "steps", title: "使用中心：只处理客户状态", items: [
        "顶部直接显示客户当前能否使用；配置中租户明确显示注册后可进入基础工作台。",
        "基础功能在注册时自动发放，资源由平台统一保护，套餐订阅后续只负责升级版本和调整用量。",
        "系统安全检查只读展示进度或异常，平台总管无需人工填写功能编号、资源编号或技术证据。",
        "运行中可暂停客户使用；已暂停可恢复或归档。所有状态动作都需填写至少 8 字符的真实原因。",
      ] },
      { type: "note", title: "安全边界", text: "日常页面不再提供逐项技术验收和手工授权入口，但服务端真实审计、隔离证据与正式 active 门禁继续保留；异常由技术团队通过受控流程处理。", },
      { type: "steps", title: "自助注册记录", items: [
        "展示租户自助注册申请表：企业名称+联系人、状态、邮箱与脱敏手机号、注册时间。",
        "状态流转：待审批（pending）→ 已批准（approved）/ 已拒绝（rejected）→ 已开通（provisioned）。",
        "注册成功会自动建立「准备中」租户；店铺授权和隔离检查完成前不会开放业务数据。",
      ] },
    ],
  },
  {
    id: "runtime", anchor: "runtime", title: "运行与计量", intro: "把后台配置转成租户运行时可以验证和强制执行的有效策略：服务账号、套餐订阅、计量用量、平台 API 配额策略、打印组件安全域名。本页所有写操作都会经统一的成功/失败反馈条回显真实结果。",
    blocks: [
      { type: "steps", title: "租户服务账号", items: [
        "「新增」：只选择所属企业、账号用途和有效期；账号名称、权限范围和密钥由系统安全生成，不能手工扩大权限。",
        "新密钥只展示一次，确认已经保存后才能关闭；列表与后续接口永不回显密钥明文。",
        "「轮换」：按生成候选密钥、验证候选密钥、切换新版本、业务稳定后吊销旧版本的顺序完成；过渡期间新旧版本可并存，避免运行中断。",
        "「吊销」：点击后打开原因弹窗，填写吊销原因（至少 8 字符）确认；吊销后该账号无法再访问任何运行时接口。",
      ] },
      { type: "fields", title: "新增服务账号表单字段", head: ["字段", "填写内容 / 示例", "要求与约束"], rows: [
        ["所属企业", "下拉选择企业名称", "必选；服务账号必须绑定明确企业"],
        ["账号用途", "ERP 业务运行 / 打印组件 / 连接健康检查", "必选；系统按用途生成最小权限"],
        ["有效期", "选择到期时间或长期有效", "到期后自动拒绝运行时访问"],
      ] },
      { type: "steps", title: "套餐与订阅", items: [
        "运行页面只展示客户订阅与已发布套餐版本数量；套餐配置请点击「前往套餐目录」进入正式套餐目录维护。",
        "订阅调整：进入「订阅中心」发起正式变更单，完成预览、提交、审批和生效；运行页面仅展示当前运行口径，不再提供旧版即时分配入口。",
      ] },
      { type: "steps", title: "计量器与累计用量", items: [
        "「计量器」按钮：正式计量目录尚未接入，本阶段暂不支持手工新建计量器；目录接入后将从正式计量目录中选择计量项，无需填写任何计量编号。",
        "下方按计量器展示累计用量：每个计量器对应租户运行时上报的用量汇总，日常无需查看内部编号。",
      ] },
      { type: "steps", title: "平台接口配额策略", items: [
        "面板展示各平台配额：平台名称下面包含每秒上限、每天预算、预警比例、限流暂停上限等配置。",
        "编辑字段后点「保存并下发」，服务端校验后保存并写入 outbox 事件，随后自动刷新页面数据。",
        "控制面未配置时显示内置默认值（微信小店/淘宝/天猫/京东/拼多多/抖音/快手/小红书/阿里巴巴），ERP 使用默认策略。",
      ] },
      { type: "fields", title: "配额策略字段（每平台）", head: ["字段", "含义", "说明"], rows: [
        ["每秒上限", "应用级每秒请求数上限", "如微信小店/抖音 10、快手 5、淘宝/天猫 8、拼多多 10"],
        ["每天预算", "每租户每日调用预算", "0 表示不限制每天预算，只限制每秒上限"],
        ["预警比例", "达到预算多少比例时提醒", "如 0.8 = 预算用掉 80% 时提醒"],
        ["限流暂停上限", "遇到平台限流后最长暂停多久", "默认 15 分钟，防止无限退避"],
      ] },
      { type: "steps", title: "打印组件安全域名（打印服务授权）", items: [
        "本面板由平台总管签发并下发到「亿开灵动智能多平台打印组件」，控制哪些网站可以访问本机打印服务。",
        "每行填写一个允许访问打印组件的网站地址；默认填入本机开发地址与生产地址。",
        "修改后点「保存并签发新版本」，生成新的策略版本（显示当前 vN 与更新时间）。",
      ] },
      { type: "note", title: "生产必读", text: "生产环境必须保留 https://erp.ekaiwork.com。漏掉生产域名会导致 ERP 无法调用本机打印服务。", },
      { type: "p", text: "页面底部有「刷新运行时数据」按钮，可手动重新拉取所有区块数据。" },
    ],
  },
  {
    id: "integrations", anchor: "integrations", title: "连接投递", intro: "管理平台连接、事件接收地址与事件投递链路。平台连接只引用凭据保险库中的凭据，页面任何位置都不读取密钥明文。",
    blocks: [
      { type: "steps", title: "平台连接", items: [
        "页头「平台连接」按钮：从带官方图标的中文平台目录选择目标平台，再确认授权说明、回调地址、权限范围和测试步骤。",
        "列表每条显示连接名称、平台、真实探测状态、证据时间、耗时、失败分类和下一步建议。",
        "「验证连接」由服务端执行真实探测并记录证据；真实探测尚未接入时明确显示暂不支持，绝不会人工写绿。",
      ] },
      { type: "fields", title: "创建平台连接 / 事件接收地址输入项", head: ["输入项", "填写内容 / 示例", "要求与约束"], rows: [
        ["平台名称", "下拉选择：微信 / 抖音 / 快手电商 等", "必选；从中文平台目录中选择"],
        ["连接名称", "如：生产主连接", "必填；建议使用中文业务名，用于区分同一平台的多条连接"],
        ["事件接收地址", "https://example.com/hooks/cp", "必填；生产必须为 HTTPS；平台侧做安全投递校验"],
        ["订阅事件", "勾选：审计记录已创建 / 事件投递结果 等中文事件", "必选；从正式中文事件目录中复选，可多选"],
      ] },
      { type: "steps", title: "事件接收地址", items: [
        "列表显示每个端点的 URL、订阅事件（中文名称）、状态徽标。",
        "创建后事件会先进入投递队列，再安全发送；失败会自动重试，超过上限后进入死信。",
      ] },
      { type: "steps", title: "事件投递队列", items: [
        "展示最近 200 条事件投递记录（页面列表展示最近 12 条）。",
        "每条显示事件类型、聚合类型、尝试次数、状态徽标。",
        "状态为 failed（失败）或 dead（死信）的事件，行尾提供重试按钮，点击后立即重新投递该事件。",
      ] },
    ],
  },
  {
    id: "service-config-platform", anchor: "service-config-platform", title: "服务配置中心：电商平台应用", intro: "在「服务配置中心」集中管理抖店、小红书、淘宝/天猫、拼多多、快手电商、微信小店等官方平台应用。普通运营只看是否可用、最近更新时间和下一步；安全管理员才看到密钥录入与轮换。",
    blocks: [
      { type: "p", text: "页面顶部明确提示：保存配置不等于已经连接官方；应用审核、服务发布、权限包、回调登记和商家授权仍必须到对应官方后台完成。每个平台卡片下方列出「官方连接前还需完成」的步骤清单与官方入口链接。" },
      { type: "fields", title: "通用字段与操作", head: ["内容", "说明"], rows: [
        ["授权返回地址", "系统按正式公网 HTTPS 域名生成并只读展示，复制到官方平台后台登记；普通运营不编辑地址"],
        ["密钥字段", "各平台按需填写；已配置字段显示绿色「已配置」标记，留空表示保持当前密钥，填写新值则创建新凭据版本"],
        ["版本与安全校验尾号", "只读展示配置版本和安全校验尾号，只证明密钥已安全保存；服务是否可用以真实验证和已启用状态为准"],
        ["状态徽标", "未配置 / 已保存 / 已验证 / 验证失败"],
        ["保存按钮", "点「保存平台配置」后加密写入保险库并生成版本；所有必填项完整后才可提交"],
      ] },
      { type: "steps", title: "上线五步", items: [
        "选择要接入的平台。",
        "保存平台配置，确认状态变成已保存。",
        "按页面提示到官方后台完成审核、权限、商家授权和返回地址登记，再执行系统验证。",
        "通过分步向导申请并显式启用。",
        "回到服务总览确认显示已启用；保存或验证通过都不等于已经运行。",
      ] },
      { type: "fields", title: "抖店开放平台（douyin）", head: ["字段", "说明"], rows: [
        ["服务市场编号", "从抖店开放平台复制，启动授权会话需要"],
        ["应用编号", "从抖店开放平台复制，接口签名会用到"],
        ["应用密钥", "从抖店开放平台复制，写入保险库，不回显"],
        ["应用使用地址", "复制页面只读展示的正式 HTTPS 授权返回地址"],
        ["官方前提交", "工具型应用与服务发布审核通过；商家已订购服务且在有效期；开放平台登记应用使用地址并开通所需 API 权限"],
      ] },
      { type: "fields", title: "小红书开放平台（xhs）", head: ["字段", "说明"], rows: [
        ["应用编号", "从小红书开放平台复制，授权时使用"],
        ["应用密钥", "从小红书开放平台复制，写入保险库"],
        ["授权返回地址", "复制页面只读展示的正式 HTTPS 授权返回地址"],
        ["官方前提交", "服务商应用审核并取得生产凭据；在平台后台登记精确回调地址；完成沙箱/联调与正式 API 权限开通"],
      ] },
      { type: "fields", title: "淘宝 / 天猫开放平台（taobao，双卡图标含天猫）", head: ["字段", "说明"], rows: [
        ["应用编号", "从淘宝开放平台复制，平台接口调用会用到"],
        ["应用密钥", "从淘宝开放平台复制，写入保险库"],
        ["授权返回地址", "复制页面只读展示的正式 HTTPS 授权返回地址"],
        ["官方前提交", "应用创建与类目/资质审核通过；授权回调地址与当前生产地址一致；申请订单、商品、库存、物流等实际 API 权限"],
      ] },
      { type: "fields", title: "拼多多开放平台（pdd）", head: ["字段", "说明"], rows: [
        ["应用编号", "从拼多多开放平台复制"],
        ["应用密钥", "从拼多多开放平台复制，写入保险库"],
        ["授权返回地址", "复制页面只读展示的正式 HTTPS 授权返回地址"],
        ["官方前提交", "服务商应用与服务市场商品审核通过；平台登记的 redirect_uri 精确匹配；商家订购并授权所需接口范围"],
      ] },
      { type: "fields", title: "快手电商服务商（kuaishou）", head: ["字段", "说明"], rows: [
        ["应用编号", "从快手开放平台复制"],
        ["应用密钥", "从快手开放平台复制，写入保险库"],
        ["Sign Secret", "签名密钥（接口签名）"],
        ["Message Secret（可选）", "消息验签密钥，配置后用于消息推送验签"],
        ["授权返回地址", "复制页面只读展示的正式 HTTPS 授权返回地址"],
        ["官方前提交", "电商服务商应用审核通过；登记回调地址并申请所需 merchant scopes；商家订购服务并完成授权"],
      ] },
      { type: "fields", title: "微信小店（wechat_shop，消息推送 + 多商户预留）", head: ["字段", "说明"], rows: [
        ["AppID", "微信小店消息推送接入 AppID"],
        ["消息 Token", "消息推送验证 Token"],
        ["EncodingAESKey", "43 位字母或数字的消息加解密密钥"],
        ["第三方平台 AppID / AppSecret（可选项）", "微信开放平台第三方平台凭据；配置后走多商户 component 授权链路"],
        ["消息推送地址", "复制页面只读展示的正式 HTTPS 消息接收地址"],
        ["官方前提交", "当前三项必填仅完成单店消息验签配置；多商户服务商还需微信开放平台第三方平台审核；component_verify_ticket、预授权码和授权方 token 链路完成前不会标记为已连接"],
      ] },
    ],
  },
  {
    id: "service-config-ai", anchor: "service-config-ai", title: "服务配置中心：AI 模型服务", intro: "在「服务配置中心」集中维护总管后台和 ERP 智能体可用的模型账号、默认模型、可用性测试、价格目录、预算和降级策略。普通运营只看当前默认、是否可用、余额/预算风险和最近测试时间。",
    blocks: [
      { type: "p", text: "顶部显示模型服务整体状态；只有保存、连通测试、设为默认、预算策略和显式启用都完成后，租户侧智能体才按平台策略使用模型。密钥和技术地址只在安全管理员的高级设置里处理。" },
      { type: "fields", title: "模型服务卡片", head: ["服务", "日常看什么", "启用前必须确认"], rows: [
        ["通义千问 / DeepSeek / Kimi / 智谱 GLM / 小米 MiMo", "是否已保存、是否为当前默认、是否已启用、最近测试时间、预算风险", "模型账号密钥已保存、模型名称正确、真实连通测试通过，并已显式启用"],
        ["自定义兼容模型", "服务名称、当前状态、是否已启用、最近测试时间、预算风险", "只使用公网 HTTPS 接口地址；本机、内网和普通 HTTP 地址不能作为生产配置，启用前必须真实测试通过"],
      ] },
      { type: "steps", title: "配置 / 轮换单个服务商", items: [
        "每张卡片填写模型账号密钥（新增时必填，至少 8 位）、模型接口地址（高级设置）和默认模型。",
        "未配置的服务商点「保存配置」；已配置的服务商点「轮换配置」（生成新的安全版本）。",
        "保存成功后先点「连通测试」；测试通过后，再点击「设为默认」。",
        "通过分步向导申请并显式启用，回到服务总览确认显示已启用；保存、测试通过或设为默认都不能单独代表已经运行。",
        "保存后提示「配置已加密保存并生成新版本」；版本与安全校验尾号只读显示在卡片顶部。",
      ] },
      { type: "note", title: "注意", text: "更新现有模型服务时必须重新提交完整配置。保存配置不会自动切换默认模型，也不代表已经运行；需要先连通测试、再手动点「设为默认」，最后申请并显式启用。租户端不会显示模型密钥、内部路由或接口地址。", },
    ],
  },
  {
    id: "service-config-sms", anchor: "service-config-sms", title: "服务配置中心：登录与短信", intro: "在「服务配置中心」集中管理验证码短信和微信扫码登录。短信与微信登录分开显示各自状态和更新时间，不能共用一个看起来正常的状态。",
    blocks: [
      { type: "fields", title: "验证码短信配置字段", head: ["字段", "填写内容 / 示例", "要求与约束"], rows: [
        ["运行模式", "阿里云真实发送 / 测试不发短信", "测试模式下其余密钥字段非必填，用于联调验证"],
        ["短信账号编号", "以 LTAI 开头", "真实发送时必填；至少 8 位"],
        ["短信账号密钥", "阿里云短信账号密钥", "真实发送时必填；至少 8 位"],
        ["短信签名", "阿里云控制台已报备的签名", "真实发送时必填，如「艺卡云」"],
        ["短信模板编号", "如 SMS_123456", "真实发送时必填"],
        ["服务区域", "cn-hangzhou", "默认 cn-hangzhou；测试模式时禁用"],
        ["短信接口地址", "dysmsapi.aliyuncs.com", "默认值；测试模式时禁用"],
      ] },
      { type: "steps", title: "保存 / 轮换", items: [
        "未配置时点「保存配置」；已配置时点「轮换配置」（需重新提交完整参数）。",
        "保存后提示「配置已加密保存并生成新版本」。真实发送未通过测试时，页面会在启用前明确提示未就绪，不会拖到审批阶段才失败。",
        "点击「发送测试短信」必须得到真实发送结果或明确失败原因；保存成功不等于短信已经可用于正式登录。",
        "短信必须按选择服务、保存、真实发送测试、申请并显式启用、确认已启用五步完成，才能作为正式登录通道使用。",
      ] },
      { type: "fields", title: "微信扫码登录配置字段", head: ["字段", "填写内容 / 示例", "要求与约束"], rows: [
        ["启用微信扫码登录", "勾选 / 不勾选", "勾选后登录页可使用微信扫码登录"],
        ["微信应用编号", "从微信开放平台复制", "启用时必填；保存后不回显"],
        ["微信应用密钥", "从微信开放平台复制", "启用时必填；保存后不回显"],
        ["授权回调地址", "https://erp.ekaiwork.com/api/auth/wechat/web/callback", "必须和微信开放平台后台登记的地址一致"],
      ] },
      { type: "note", title: "独立状态", text: "微信扫码登录有独立状态、独立更新时间和独立失败原因；短信正常不代表微信登录也正常。", },
      { type: "note", title: "启用要求", text: "微信扫码登录也必须按选择服务、保存、系统回调验证、申请并显式启用、确认已启用五步完成；保存或短信通过不能替代微信登录启用。", },
    ],
  },
  {
    id: "enterprise", anchor: "enterprise", title: "企业治理", intro: "集中管理企业登录、成员同步、审计外发和通用双人审批。",
    blocks: [
      { type: "steps", title: "企业身份接入", items: [
        "「配置」：从下拉选择租户、填写已验证域名（如 example.com），为租户建立企业登录配置。",
        "表格列：租户、域名（多个逗号分隔）、单点登录状态、成员同步状态。",
      ] },
      { type: "steps", title: "审计流", items: [
        "页头「审计外发」按钮：输入审计接收地址（生产必须 HTTPS），把审计事件转发到外部系统。",
        "列表显示目标地址、类型、状态。",
      ] },
      { type: "steps", title: "通用审批（四眼审批）", items: [
        "高风险操作（如吊销凭据、暂停租户、代管授权等）在对应业务页发起时自动弹出审批确认，页面只显示业务对象名称和动作，无需填写任何编号。",
        "待审批（pending）申请点「批准 / 拒绝」，需输入审批意见（至少 8 字符）。",
        "申请人与审批人不能是同一人；已批准的申请在对应动作执行后写入执行时间审计。",
      ] },
    ],
  },
  {
    id: "operations", anchor: "operations", title: "运营治理", intro: "管理事故、维护窗口、灰度开关与组织策略；所有变更统一进入审计和可靠事件链。页面顶部有统一的操作结果反馈条。",
    blocks: [
      { type: "steps", title: "事故中心", items: [
        "页头「登记事故」：输入事故标题、事故描述（至少 12 字符，严重级别默认为重要事故）。",
        "已登记时可点「监控中」；未解决时点「解决」，均需填写流转备注（至少 8 字）。",
      ] },
      { type: "steps", title: "维护窗口", items: [
        "页头「维护窗口」：输入维护标题、维护说明（至少 12 字符）；系统生成从现在起 1 小时后开始、持续 1 小时的窗口。",
        "列表显示标题、开始时间、持续分钟数。",
      ] },
      { type: "steps", title: "功能开关", items: [
        "「新增」：只需填写功能名称，系统编号由系统自动生成，页面上不需要填写任何编号。",
        "每条开关显示功能名称、范围（租户名称或「全平台」）、灰度百分比；点「启用 / 停用」切换；技术编号折叠在「技术编号」中，日常无需查看。",
      ] },
      { type: "steps", title: "组织层级", items: [
        "「组织单元」：输入组织单元名称，创建根级组织单元（父级留空）。",
        "列表显示组织单元名称与根级/下级标识；编辑/删除能力属后续开发边界。",
      ] },
    ],
  },
  {
    id: "credentials", anchor: "credentials", title: "凭据保险库", intro: "统一管理平台级与租户级连接凭据。采用只写入、不展示的安全模型：只显示安全校验尾号、版本和有效期，任何接口都不会返回明文。普通运营优先使用服务配置中心，不需要直接操作保险库。",
    blocks: [
      { type: "steps", title: "写入凭据", items: [
        "页面右上「写入凭据」：选择作用范围（平台级 / 租户级）、从下拉选择服务商或平台（如「微信」「抖音」「AI 默认模型」）、从下拉选择凭据用途、填写密钥内容；租户级还需从下拉选择绑定租户。",
        "提交后提示「正在加密写入」，成功后明文不会再次显示；卡片显示版本 vN、安全校验尾号、有效期、最近使用。",
      ] },
      { type: "fields", title: "写入凭据表单字段", head: ["字段", "填写内容 / 示例", "要求与约束"], rows: [
        ["作用范围", "平台级 / 租户级", "租户级必须再选择绑定租户"],
        ["服务商 / 平台", "下拉选择，如「微信」「抖音」「AI 默认模型」", "必选；告诉系统这份密钥属于哪家服务"],
        ["绑定租户（仅租户级）", "下拉选择租户名称", "租户级必选；列表来自真实租户台账，无需手输编号"],
        ["凭据名称", "下拉选择，如「AI 模型服务密钥」「短信发送密钥」", "必选；凭据的业务用途"],
        ["密钥明文", "多行文本", "必填；仅本次提交可见，AES-256-GCM 加密入库"],
      ] },
      { type: "steps", title: "轮换与吊销", items: [
        "轮换：输入新密钥与轮换原因（至少 8 字符），创建 vN+1，当前版本立即退役并保留审计证据。",
        "吊销：输入吊销原因（至少 8 字符），凭据停止使用；已吊销凭据的轮换/吊销按钮禁用。",
      ] },
    ],
  },
  {
    id: "admins", anchor: "admins", title: "平台管理员", intro: "管理平台级管理员身份（与租户账号完全分离）。角色只授予完成平台治理所需的最小动作集合；账号删除仅平台负责人可执行。",
    blocks: [
      { type: "fields", title: "新增管理员表单字段", head: ["字段", "填写内容 / 示例", "要求与约束"], rows: [
        ["显示名称", "如 平台运维张三", "必填"],
        ["邮箱", "admin@example.com", "必填，格式校验"],
        ["平台角色", "只读审计员 / 代管操作员 / 租户运营员 / 安全管理员 / 平台负责人", "建议从只读审计员开始，确有职责需要再授予写权限"],
        ["初始密码", "至少 10 位，包含大小写字母和数字", "必填；首次登录使用"],
      ] },
      { type: "steps", title: "列表与编辑", items: [
        "列表列：管理员（显示名+邮箱）、平台角色、状态、最近登录、创建时间。",
        "每行「管理」按钮：按提示依次选择新的平台角色（默认保留当前角色）、确认是否同时停用/恢复该管理员、填写变更原因（至少 8 字符）。",
        "角色与停用/恢复可同时变更，任何变更都会写入审计链。",
        "平台负责人每行可使用「删除」按钮：二次确认后填写删除原因，账号会立即退出后台、列表不再显示，历史审计记录保留。",
        "不能删除当前登录账号，也不能删除最后一个活跃平台负责人。",
      ] },
    ],
  },
  {
    id: "support", anchor: "support", title: "受控代管", intro: "平台人员不能永久加入租户。每次代管访问都绑定租户、原因、指定操作员、双人审批与最长 120 分钟有效期。",
    blocks: [
      { type: "steps", title: "申请短时代管", items: [
        "页头「申请代管」：从下拉选择租户、从下拉选择操作员、填写有效分钟数（5–120，默认 30）和代管原因（至少 12 字符）。",
        "提交后状态为 pending（待审批）。",
      ] },
      { type: "fields", title: "申请短时代管表单字段", head: ["字段", "填写内容 / 示例", "要求与约束"], rows: [
        ["租户", "下拉选择租户名称", "必选；明确要代管的目标租户，无需手输编号"],
        ["操作员", "下拉选择操作员姓名", "必选；指定执行代管的平台操作员，无需手输编号"],
        ["有效分钟数", "30（默认）", "5–120 分钟，超出范围会被拒绝"],
        ["代管原因", "如：协助排查订单同步问题", "必填，至少 12 字符"],
      ] },
      { type: "steps", title: "代管记录操作", items: [
        "pending：另一管理员点「批准」转 approved（双人审批，审批人不能是申请人）。",
        "已批准：只有被指定操作员本人点「激活」，激活响应一次性显示短时代管票据（含租户与过期时间），通过「复制短时票据」交给租户端交换接口创建代管会话。",
        "未撤销/未过期/未拒绝的记录可随时点「撤销」转 revoked。",
      ] },
      { type: "note", title: "边界", text: "激活只建立控制面授权；租户端必须验证短时交换票据后才能创建代管会话。", },
    ],
  },
  {
    id: "audit", anchor: "audit", title: "审计链", intro: "按时间展示不可静默改写的操作证据，每条事件都有防篡改校验值（展示最近 200 条）。",
    blocks: [
      { type: "steps", title: "查看与验证", items: [
        "事件列表显示序号、操作类型、时间、操作人邮箱、影响对象、防篡改校验值、操作追踪号、相关租户（如有）。",
        "点「验证完整链」对全链哈希校验：通过时提示「审计链完整，已验证 N 条事件」；失败时提示「审计链在 #N 断裂」，需立即排查。",
      ] },
    ],
  },
  {
    id: "health", anchor: "health", title: "平台健康", intro: "区分服务是否在线和数据库是否可用，数据库不可用时必须明确报错，不能显示假正常。",
    blocks: [
      { type: "table", head: ["卡片", "探针", "说明"], rows: [
        ["接口服务", "在线检查", "服务是否还在运行"],
        ["控制面数据库", "可用", "真实查询已完成，数据库真实可读"],
        ["运行模式", "严格校验", "无离线假数据回退，缺数据库/密钥时绝不降级为演示数据"],
      ] },
    ],
  },
  {
    id: "ai-assistant", anchor: "ai-assistant", title: "AI 平台助手", intro: "全局悬浮 AI 大脑：问答平台功能、理解当前页面、解释字段、识别风险、排查错误、一步步引导操作。只读安全边界——不能替你执行任何写操作。",
    blocks: [
      { type: "p", text: "登录后每个页面右下角都有「AI 平台助手」按钮。点开即可用中文聊天提问；助手知道总管后台每个页面的功能、填写要求、操作成功标志、常见故障和平台实时概况，也知道你当前正在浏览哪个页面。" },
      { type: "steps", title: "使用步骤", items: [
        "点击右下角紫色按钮打开助手面板。",
        "先看助手自动展示的当前页面卡片、平台风险卡片、字段入口和常见卡住点。",
        "可直接点击新手任务（如「我第一次进来」「这个页面怎么用」「平台体检」「出错了怎么办」），或在输入框输入问题后点「发送」。",
        "助手会结合完整页面知识、字段级约束和平台实时数据回答；问「这个页面怎么用」时自动针对你当前所在的页面。",
        "点右上角循环图标可清空对话重新开始。",
      ] },
      { type: "steps", title: "大脑能力", items: [
        "当前页指南：说明当前页面管什么、什么时候用、先看哪里、做完怎么看成功。",
        "字段级解释：回答字段含义、从哪里拿、示例、格式限制、填错影响和安全风险。",
        "任务式引导：按「去哪里」「点什么」「怎么填」「成功标志」「失败先查」给步骤。",
        "平台体检：根据实时概况识别临期凭据、开放代管授权、暂停租户等风险，并给处理入口。",
        "故障排查：针对 AI 不可用、按钮点不了、保存失败、租户不能激活等问题给检查顺序。",
      ] },
      { type: "note", title: "使用前必须配置", text: "助手依赖大模型服务。请先到「服务配置中心」→「AI 模型服务」保存至少一个模型服务的完整配置（模型账号密钥、模型接口地址、默认模型），通过连通测试、设为默认并显式启用后，再发一句测试问题。助手优先使用当前默认且已启用的模型服务；未配置或未启用时提问会得到明确提示。", },
      { type: "steps", title: "安全边界", items: [
        "只读问答：助手不能创建、修改、删除任何数据，只能解释和引导你自己在页面上操作。",
        "每次对话写入审计链，只记录模型服务、模型、消息数等元数据，不记录对话内容。",
        "模型密钥从加密保险库中服务端解密使用，任何接口不回显明文。",
        "助手上下文接口只读取页面知识和 dashboard 概况，不创建、不修改、不删除任何数据。",
        "所有平台角色（含只读审计角色）均可使用；对话不承载租户业务数据。",
      ] },
      { type: "p", text: "换模型：到「服务配置中心」→「AI 模型服务」保存或轮换目标服务的完整配置（模型账号密钥 + 模型接口地址 + 默认模型），连通测试通过后点击该卡片「设为默认」，再申请并显式启用；使用自定义兼容模型平台（如硅基流动）时，只填写平台提供的公网 HTTPS 接口地址、密钥和模型名。" },
    ],
  },
];

const roleTable: TableRow[] = [
  ["平台负责人", "平台最高管理者", "全部动作"],
  ["安全管理员", "安全与密钥治理", "凭据读写/轮换/吊销、管理员管理、代管审批、审计、健康、运行时、连接投递、企业治理、审批、运营、功能开关、配额"],
  ["租户运营员", "租户日常运营", "租户生命周期/授权/配额/隔离检查、应用审批、套餐订阅、用量、连接投递、企业治理、运营、功能开关、配额、打印策略读取"],
  ["代管操作员", "受控代管操作员", "申请/激活/撤销代管、只读租户与凭据元数据、审计、健康"],
  ["只读审计员", "只读审计", "总览、租户只读、应用记录、凭据元数据、管理员只读、审计、健康、用量"],
];

async function loadManualTaskGuideResponse(): Promise<ManualTaskGuideResponse> {
  const payload = await api<unknown>("/api/assistant/manual-task-guides");
  return manualTaskGuideResponseSchema.parse(payload);
}

function formatManualTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "刚刚返回，时间待确认";
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function groupedManualTasks(tasks: ManualTaskGuide[]) {
  const groups = new Map<string, { pageTitle: string; tasks: ManualTaskGuide[] }>();
  for (const task of tasks) {
    const existing = groups.get(task.sourcePage.pagePath);
    if (existing) {
      existing.tasks.push(task);
      continue;
    }
    groups.set(task.sourcePage.pagePath, { pageTitle: task.sourcePage.pageTitle, tasks: [task] });
  }
  return Array.from(groups.values());
}

function triggerManualAction(action: AssistantNextAction) {
  if (action.kind === "navigate" && action.pagePath) {
    appNavigate(action.pagePath);
    return;
  }
  if ((action.kind === "ask" || action.kind === "open_manual") && action.prompt) askAssistant(action.prompt);
}

function ManualTaskAction({ action }: { action: AssistantNextAction }) {
  if (action.kind === "navigate" && action.pagePath) {
    return <button type="button" className="button secondary" onClick={() => triggerManualAction(action)}>{action.label}</button>;
  }
  if ((action.kind === "ask" || action.kind === "open_manual") && action.prompt) {
    return <button type="button" className="button secondary" onClick={() => triggerManualAction(action)}>{action.label}</button>;
  }
  return <span className="manual-task-unavailable">这个下一步暂时不可直接打开，请先联系平台技术支持确认。</span>;
}

function ManualTaskList({ title, items }: { title: string; items: string[] }) {
  return <div className="manual-task-list"><strong>{title}</strong><ul>{items.map((item) => <li key={item}>{renderInline(item)}</li>)}</ul></div>;
}

function ManualTaskGuideBoard({ response }: { response: ManualTaskGuideResponse }) {
  return <section id="manual-task-guides" className="panel manual-section manual-task-guide-section">
    <div className="section-title">
      <div><ListChecks size={17} /><h2>按业务任务处理</h2></div>
      <span>数据更新时间：{formatManualTime(response.generatedAt)}</span>
    </div>
    <p className="manual-intro">这些指南由服务端按当前页面帮助合同生成，先告诉你能做什么、什么时候用，再给做错后的恢复办法。没有记录时也会给出真实下一步入口。</p>
    <div className="manual-task-groups">
      {groupedManualTasks(response.tasks).map((group) => <article key={group.pageTitle} className="manual-task-group">
        <header><BookOpen size={16} /><strong>{group.pageTitle}</strong><span>{group.tasks.length} 个任务</span></header>
        <div className="manual-task-cards">
          {group.tasks.map((task) => <article key={task.taskKey} className="manual-task-card">
            <div className="manual-task-card-head">
              <h3>{task.title}</h3>
              <span>来源：{task.sourcePage.pageTitle}</span>
            </div>
            <div className="manual-task-columns">
              <ManualTaskList title="能做什么" items={task.whatYouCanDo} />
              <ManualTaskList title="什么时候用" items={task.whenToUse} />
              <ManualTaskList title="做错如何恢复" items={task.howToRecover} />
            </div>
            <footer>
              <span>{task.emptyState.message}</span>
              <ManualTaskAction action={task.emptyState.nextAction} />
            </footer>
          </article>)}
        </div>
      </article>)}
    </div>
  </section>;
}

function renderInline(text: string): ReactNode {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("`") && part.endsWith("`")) return <code key={index}>{part.slice(1, -1)}</code>;
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={index}>{part.slice(2, -2)}</strong>;
    return <span key={index}>{part}</span>;
  });
}

function Block({ block }: { block: DocBlock }) {
  switch (block.type) {
    case "p":
      return <p>{renderInline(block.text)}</p>;
    case "steps":
      return <div className="manual-block"><h3><ListChecks size={15} />{block.title}</h3><ol>{block.items.map((item, index) => <li key={index}>{renderInline(item)}</li>)}</ol></div>;
    case "fields":
      return <div className="manual-block"><h3><ListChecks size={15} />{block.title}</h3><table className="compact-table manual-table"><thead><tr>{block.head.map((cell, index) => <th key={index}>{cell}</th>)}</tr></thead><tbody>{block.rows.map((row, rIndex) => <tr key={rIndex}>{row.map((cell, cIndex) => <td key={cIndex}>{cell}</td>)}</tr>)}</tbody></table></div>;
    case "note":
      return <div className="manual-note"><Info size={15} /><div><strong>{block.title}</strong><span>{renderInline(block.text)}</span></div></div>;
    case "table":
      return <table className="compact-table manual-table"><thead><tr>{block.head.map((cell, index) => <th key={index}>{cell}</th>)}</tr></thead><tbody>{block.rows.map((row, rIndex) => <tr key={rIndex}>{row.map((cell, cIndex) => <td key={cIndex}>{cell}</td>)}</tr>)}</tbody></table>;
  }
}

export function UserManualPage() {
  const { state, reload } = useRemote(loadManualTaskGuideResponse, (value) => value.tasks.length === 0);
  return <div className="page">
    <PageHeader icon={BookOpen} title="使用说明" description="总管后台每个页面的功能与操作步骤。本页所有描述均对照真实界面；页面含写操作时以真实结果反馈为准。" />
    <div className="security-banner"><ShieldCheck /><div><strong>平台级安全边界</strong><span>本后台不承载租户业务数据；高层级变更均写入审计链，密钥永不回显明文。</span></div></div>
    <nav className="manual-nav"><a href="#manual-task-guides"><ListChecks size={14} />按任务处理</a>{sections.map((section) => <a key={section.id} href={`#manual-${section.anchor}`}><BookOpen size={14} />{section.title}</a>)}<a href="#manual-role"><FileText size={14} />角色权限速查</a><a href="#manual-rules"><FileText size={14} />通用规则</a></nav>
    <div className="manual-stack">
      <RemotePageState
        state={state}
        onRetry={reload}
        empty={<button type="button" className="button secondary" onClick={() => appNavigate("/manual#manual-rules")}>查看通用规则</button>}
        emptyHint="当前没有可展示的任务指南，可先查看通用安全规则或稍后重试。"
      >
        {(guideResponse) => <ManualTaskGuideBoard response={guideResponse} />}
      </RemotePageState>
      {sections.map((section) => <section key={section.id} id={`manual-${section.anchor}`} className="panel manual-section"><div className="section-title"><div><BookOpen size={17} /><h2>{section.title}</h2></div></div><p className="manual-intro">{section.intro}</p><div className="manual-blocks">{section.blocks.map((block, index) => <Block key={index} block={block} />)}</div></section>)}
      <section id="manual-role" className="panel manual-section"><div className="section-title"><div><ShieldCheck size={17} /><h2>附录 A：平台角色与权限速查</h2></div></div><table className="compact-table manual-table"><thead><tr><th>角色</th><th>典型用途</th><th>主要权限范围</th></tr></thead><tbody>{roleTable.map((row, rIndex) => <tr key={rIndex}>{row.map((cell, cIndex) => <td key={cIndex}>{cell}</td>)}</tr>)}</tbody></table><p className="manual-intro">菜单只按权限收窄展示；真正的高风险写操作在服务端按动作权限 + 审计强制校验，不能靠隐藏菜单绕过。</p></section>
      <section id="manual-rules" className="panel manual-section"><div className="section-title"><div><FileText size={17} /><h2>附录 B：通用规则与安全边界</h2></div></div><ol className="manual-plain-list"><li>写操作均留痕：创建、变更、暂停、归档、轮换、吊销、审批、代管激活等全部写入审计链，失败会回显服务端原因，不存在假成功。</li><li>密钥永不回显：任何页面、任何接口都不返回凭据明文；只返回版本、安全校验尾号、有效期等元数据。新密钥（服务账号、双因素认证一次性密钥）只显示一次。</li><li>平台级数据跨租户可见（租户列表、审计链），但控制面不承载任何租户业务数据；租户业务读写只能通过租户绑定服务账号的加密契约进行。</li><li>约束性校验由服务端强制：原因类输入（状态变更 ≥8 字符、证据 ≥12 字符、审批意见 ≥8 字符）、可行性校验（软限制 ≤ 硬限制、未授权不能启用、激活需隔离准备度 100%）。</li><li>本地预览模式运行真实进程内数据库，只监听本机、重启后数据清空，仅用于验收，不得用于生产。</li></ol></section>
    </div>
  </div>;
}
